/**
 * Shared Leaflet tile-layer config + a bulletproof foreground-reload hook.
 *
 * The reload strategy is NUCLEAR: when the app returns to the foreground
 * we fully destroy and recreate the Leaflet map instance. This is the
 * only approach that survives iOS WKWebView's aggressive tile purging —
 * partial fixes (tileLayer.redraw, remove+add tile layer) work in 90%
 * of cases but leave users with a gray map in the remaining 10%.
 *
 * The caller is responsible for:
 *   - passing a rebuild callback that re-creates its polyline/marker
 *     after the fresh map is available
 *   - NOT holding onto any old leaflet layer reference outside the
 *     provided refs (they will be invalidated after each rebuild)
 *
 * Diagnostic mode: set `localStorage.setItem('slimmy_debug_map_resume','1')`
 * in the browser / Safari Web Inspector to log every resume event + the
 * time it took to rebuild.
 */
import { useEffect, useRef } from 'react';

export const TILE_LAYER_URL =
  'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png';

export const TILE_LAYER_OPTS = {
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  subdomains: 'abcd',
  maxZoom: 20,
  crossOrigin: true,
};

const DEBUG = () => {
  try {
    return typeof window !== 'undefined'
      && window.localStorage
      && window.localStorage.getItem('slimmy_debug_map_resume') === '1';
  } catch { return false; }
};

const dlog = (...args) => {
  if (DEBUG()) {
    // eslint-disable-next-line no-console
    console.log('[slimmy/map-resume]', ...args);
  }
};

export function buildTileLayer() {
  if (typeof window === 'undefined' || !window.L) return null;
  return window.L.tileLayer(TILE_LAYER_URL, TILE_LAYER_OPTS);
}

/**
 * @typedef {Object} MapResumeConfig
 * @property {React.MutableRefObject}  mapRef              - DOM <div> ref for the map container.
 * @property {React.MutableRefObject}  mapInstanceRef      - current L.Map instance.
 * @property {React.MutableRefObject}  tileLayerRef        - current L.TileLayer instance.
 * @property {() => void}              [onAfterRebuild]    - fired AFTER the new map is ready so the caller can re-add polyline/marker.
 * @property {() => ({center, zoom})}  [getViewport]       - optional. Returns the viewport to restore; defaults to current map's center/zoom.
 * @property {object}                  [mapOptions]        - extra options forwarded to L.map() on init/rebuild.
 */

/**
 * Mount a foreground-resume reloader on the map. Returns a `manualReload()`
 * function the caller can wire to a UI button as an extra safety net.
 */
export function useMapResumeReload(config) {
  const {
    mapRef,
    mapInstanceRef,
    tileLayerRef,
    onAfterRebuild,
    getViewport,
    mapOptions,
  } = config;

  // Stable holder so the hook can export a callable identity even before
  // the effect mounts.
  const reloadRef = useRef(() => {});

  useEffect(() => {
    let pending = null;

    const rebuildMap = (sourceLabel) => {
      const container = mapRef && mapRef.current;
      const oldMap = mapInstanceRef.current;
      if (!container || typeof window === 'undefined' || !window.L) {
        dlog('skip: container or leaflet missing');
        return;
      }
      // Capture viewport BEFORE we destroy the old instance.
      let savedCenter = [41.9028, 12.4964];
      let savedZoom = 15;
      try {
        if (typeof getViewport === 'function') {
          const vp = getViewport();
          if (vp && vp.center) savedCenter = vp.center;
          if (vp && typeof vp.zoom === 'number') savedZoom = vp.zoom;
        } else if (oldMap) {
          const c = oldMap.getCenter();
          const z = oldMap.getZoom();
          if (c) savedCenter = [c.lat, c.lng];
          if (typeof z === 'number') savedZoom = z;
        }
      } catch { /* fallback to defaults */ }

      const t0 = performance.now();

      // 1. Fully destroy the old map. This is the ONLY way to force iOS
      //    WKWebView to drop every cached tile <img> — half-measures like
      //    removing/re-adding the tile layer leave zombie tiles in memory.
      try {
        if (oldMap) {
          oldMap.remove();
        }
      } catch (e) {
        dlog('destroy failed (continuing)', String(e));
      }
      mapInstanceRef.current = null;
      tileLayerRef.current = null;

      // 2. Clear the container's children. Leaflet leaves a residual
      //    `_leaflet_id` attribute which prevents re-initialisation.
      try {
        container.innerHTML = '';
        if (container._leaflet_id) delete container._leaflet_id;
      } catch { /* noop */ }

      // 3. Build a brand-new map with the exact same config.
      try {
        const freshMap = window.L.map(container, {
          center: savedCenter,
          zoom: savedZoom,
          zoomControl: false,
          ...(mapOptions || {}),
        });
        const freshTile = buildTileLayer().addTo(freshMap);
        mapInstanceRef.current = freshMap;
        tileLayerRef.current = freshTile;

        // 4. Nudge a resize so tiles start fetching immediately.
        setTimeout(() => {
          try { freshMap.invalidateSize(true); } catch { /* noop */ }
        }, 30);

        // 5. Let the caller rewire polyline/marker to the fresh instance.
        if (typeof onAfterRebuild === 'function') {
          try { onAfterRebuild(); } catch (e) { dlog('onAfterRebuild error', String(e)); }
        }

        dlog(`rebuilt in ${(performance.now() - t0).toFixed(0)}ms via ${sourceLabel}`);
      } catch (e) {
        dlog('rebuild failed', String(e));
      }
    };

    const reloadMap = (sourceLabel) => {
      dlog('event', sourceLabel);
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => {
        pending = null;
        rebuildMap(sourceLabel);
      }, 60);
    };

    reloadRef.current = () => reloadMap('manual');

    const onVisibility = () => {
      if (document.visibilityState === 'visible') reloadMap('visibilitychange');
    };
    const onPageShow = () => reloadMap('pageshow');
    const onFocus = () => reloadMap('focus');
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('focus', onFocus);

    let capHandle = null;
    (async () => {
      try {
        const { appLifecycle } = await import('./native');
        capHandle = await appLifecycle.onStateChange(({ isActive }) => {
          if (isActive) reloadMap('capacitor-appStateChange');
        });
      } catch {
        // Capacitor not available on the web build — ignore.
      }
    })();

    return () => {
      if (pending) clearTimeout(pending);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('focus', onFocus);
      try { capHandle && capHandle.remove && capHandle.remove(); } catch { /* noop */ }
    };
    // We intentionally exclude the callback props from deps: the hook
    // reads them via closure but they shouldn't re-mount the listeners.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, mapInstanceRef, tileLayerRef]);

  return () => reloadRef.current && reloadRef.current();
}
